/**
 * Properties API layer with adapters for backend integration
 */
import { apiClient } from '@services/api/client';
import { ENDPOINTS } from '@config/endpoints';
import type {
  Property,
  PropertyFilters,
  PropertiesResponse,
  District,
  LocalizedField,
  ApiListing,
  ApiListingsResponse,
  ApiDistrictsResponse,
  ApiPropertyTypesResponse,
} from '@/types';

// =============================================================================
// Adapters: Transform API responses to frontend format
// =============================================================================

/**
 * Transform API listing to frontend Property format
 */
function adaptListing(apiListing: ApiListing): Property {
  return {
    id: apiListing.id,
    title: apiListing.title,
    description: apiListing.description,
    address: apiListing.address,
    shortCode: apiListing.short_code,
    propertyType: apiListing.property_type,
    status: apiListing.status,
    district: apiListing.district,
    city: apiListing.city,
    features: apiListing.features,
    price: apiListing.price,
    bedrooms: apiListing.bedrooms,
    bathrooms: apiListing.bathrooms,
    area: apiListing.area,
    yearBuilt: apiListing.year_built,
    photos: apiListing.photos || [],
  };
}

/**
 * Transform API paginated response to frontend format
 */
function adaptListingsResponse(apiResponse: ApiListingsResponse): PropertiesResponse {
  return {
    data: apiResponse.items.map(adaptListing),
    total: apiResponse.total,
    page: apiResponse.page,
    pageSize: apiResponse.page_size,
    totalPages: apiResponse.pages,
  };
}

/**
 * Transform API district to frontend format
 */
function adaptDistrict(apiDistrict: { code: string; label: string; listing_count?: number | null }): District {
  return {
    code: apiDistrict.code,
    label: apiDistrict.label,
    listingCount: apiDistrict.listing_count ?? undefined,
  };
}

/**
 * Transform frontend filters to API query params (camelCase -> snake_case)
 */
function adaptFiltersToApi(filters: PropertyFilters): Record<string, string | number | boolean | undefined> {
  const apiParams: Record<string, string | number | boolean | undefined> = {};

  if (filters.search) apiParams.search = filters.search;
  if (filters.district) apiParams.district = filters.district;
  if (filters.propertyType) apiParams.property_type = filters.propertyType;
  if (filters.city) apiParams.city = filters.city;
  if (filters.minPrice !== undefined) apiParams.min_price = filters.minPrice;
  if (filters.maxPrice !== undefined) apiParams.max_price = filters.maxPrice;
  if (filters.minBedrooms !== undefined) apiParams.min_bedrooms = filters.minBedrooms;
  if (filters.maxBedrooms !== undefined) apiParams.max_bedrooms = filters.maxBedrooms;
  if (filters.minArea !== undefined) apiParams.min_area = filters.minArea;
  if (filters.maxArea !== undefined) apiParams.max_area = filters.maxArea;
  if (filters.status) apiParams.status = filters.status;
  if (filters.page !== undefined) apiParams.page = filters.page;
  if (filters.pageSize !== undefined) apiParams.page_size = filters.pageSize;

  return apiParams;
}

// =============================================================================
// API Service
// =============================================================================

export const propertiesApi = {
  /**
   * Get all properties with optional filters and pagination
   */
  async getProperties(filters: PropertyFilters = {}): Promise<PropertiesResponse> {
    const apiParams = adaptFiltersToApi(filters);
    const response = await apiClient.get<ApiListingsResponse>(ENDPOINTS.LISTINGS, { params: apiParams });
    return adaptListingsResponse(response);
  },

  /**
   * Get a single property by ID
   */
  async getPropertyById(id: string): Promise<Property | null> {
    try {
      const response = await apiClient.get<ApiListing>(ENDPOINTS.LISTING_DETAIL(id));
      return adaptListing(response);
    } catch (error) {
      // Return null if not found
      return null;
    }
  },

  /**
   * Get districts with optional listing counts
   */
  async getDistricts(city?: string, includeCounts: boolean = true): Promise<District[]> {
    const params: Record<string, string | boolean> = { include_counts: includeCounts };
    if (city) params.city = city;

    const response = await apiClient.get<ApiDistrictsResponse>(ENDPOINTS.DISTRICTS, { params });
    return response.districts.map(adaptDistrict);
  },

  /**
   * Get property types
   */
  async getPropertyTypes(): Promise<LocalizedField[]> {
    const response = await apiClient.get<ApiPropertyTypesResponse>(ENDPOINTS.PROPERTY_TYPES);
    return response.property_types;
  },

  /**
   * Get cities
   */
  async getCities(): Promise<LocalizedField[]> {
    const response = await apiClient.get<{ cities: LocalizedField[] }>(ENDPOINTS.CITIES);
    return response.cities;
  },
};
